import type { OrderItem } from './order';

/**
 * Что можно делать с отправленной позицией.
 *
 * Повар начал готовить — продукты потрачены. Официант больше не вправе
 * менять состав или снимать строку: это уже не его решение, а вопрос
 * списания. Старший может, но с причиной, и она попадёт в отчёт.
 *
 * Правило простое, но проверять его надо в одном месте: касса,
 * телефон официанта и любой будущий экран должны отвечать одинаково.
 */

export type LineAction = 'qty.up' | 'qty.down' | 'remove' | 'note' | 'course';

/**
 * Разрешено ли действие над строкой.
 *
 * `senior` — есть ли у человека право снимать позиции. У официанта
 * его нет, у менеджера и владельца есть.
 */
export function canTouchLine(
  item: Pick<OrderItem, 'fired' | 'removed'>,
  action: LineAction,
  senior: boolean,
): { ok: true } | { ok: false; reason: string } {
  if (item.removed) {
    return { ok: false, reason: 'Позиция уже снята' };
  }
  if (!item.fired) return { ok: true };   // на кухню не уходило — свобода

  if (senior) return { ok: true };

  // Заметка кухне после отправки бессмысленна: бегунок уже на кухне,
  // повар его прочитал и работает. Курс тоже — блюдо готовится
  const what: Record<LineAction, string> = {
    'qty.up': 'Добавьте отдельной строкой — это новый заказ для кухни',
    'qty.down': 'Блюдо уже готовят. Убрать может только старший',
    remove: 'Блюдо уже готовят. Снять может только старший',
    note: 'Бегунок уже на кухне — повар его прочитал',
    course: 'Блюдо уже в работе, очередь подачи не изменить',
  };
  return { ok: false, reason: what[action] };
}

/** Есть ли в заказе хоть одна отправленная строка. */
export function hasFired(items: Pick<OrderItem, 'fired' | 'removed'>[]): boolean {
  return items.some((i) => i.fired && !i.removed);
}

/** Сколько строк ещё не ушло на кухню — столько и уйдёт при отправке. */
export function freshCount(items: Pick<OrderItem, 'fired' | 'removed'>[]): number {
  return items.filter((i) => !i.fired && !i.removed).length;
}
